import {Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@tryghost/shade';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {OverridesPanel} from '@/components/admin/OverridesPanel';
import {Select} from '@/components/ui/Select';
import {useAdminSites} from '@/hooks/useAdminSites';
import {useDebounce} from '@/hooks/useDebounce';
import {useEffect, useState} from 'react';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import {useReactivateSite} from '@/hooks/useReactivateSite';
import {useSuspendSite} from '@/hooks/useSuspendSite';
import type {AdminSite} from '@/types/api';

function SiteStatusBadge({status}: { status: string }) {
    const colors: Record<string, { bg: string; color: string }> = {
        active: {bg: 'var(--studio-soft)', color: 'var(--tt-text-secondary)'},
        provisioning: {bg: 'rgba(239, 90, 43, 0.1)', color: 'var(--tt-accent)'},
        suspended: {bg: 'rgba(245, 11, 35, 0.1)', color: 'var(--tt-red)'},
        failed: {bg: 'var(--tt-badge-bg)', color: 'var(--tt-text-secondary)'}
    };
    const c = colors[status] || {bg: 'var(--tt-badge-bg)', color: 'var(--tt-text-secondary)'};
    return (
        <span
            style={{
                display: 'inline-block',
                fontSize: '1.1rem',
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: 40,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                background: c.bg,
                color: c.color
            }}
        >
            {status}
        </span>
    );
}

function ConfirmDialog({
    title,
    message,
    confirmLabel,
    confirmColor,
    onConfirm,
    onCancel,
    isLoading
}: {
    title: string;
    message: string;
    confirmLabel: string;
    confirmColor?: string;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--tt-modal-backdrop)'
            }}
            onClick={onCancel}
        >
            <div className="studio-panel"
                style={{background: 'var(--tt-card-bg)', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%'}}
                onClick={e => e.stopPropagation()}
            >
                <h3
                    style={{fontSize: '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px 0', letterSpacing: '-.025em'}}
                >
                    {title}
                </h3>
                <p
                    style={{
                        fontSize: '1.4rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 24px 0',
                        lineHeight: 1.5
                    }}
                >
                    {message}
                </p>
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 12}}>
                    <button className="studio-control" disabled={isLoading}
                        style={{height: 34, padding: '0 14px', borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button className="studio-control studio-control-primary" disabled={isLoading}
                        style={{background: confirmColor || 'var(--tt-accent)', cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1, fontFamily: 'inherit'}}
                        type="button"
                        onClick={onConfirm}
                    >
                        {isLoading ? 'Processing...' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

const STATUS_OPTIONS = [
    {value: '', label: 'All statuses'},
    {value: 'active', label: 'Active'},
    {value: 'provisioning', label: 'Provisioning'},
    {value: 'suspended', label: 'Suspended'},
    {value: 'failed', label: 'Failed'}
];

export function AdminSitesPage() {
    const {isMobile, isTablet} = useMediaQuery();
    const isCompact = isMobile || isTablet;
    const [selectedSite, setSelectedSite] = useState<AdminSite | null>(null);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, 300);
    const [statusFilter, setStatusFilter] = useState('');
    const [confirmAction, setConfirmAction] = useState<{
        siteId: string;
        siteName: string;
        action: 'suspend' | 'reactivate';
    } | null>(null);

    const {data, isLoading, isError, error: queryError} = useAdminSites(page, debouncedSearch, statusFilter);
    const suspendMutation = useSuspendSite();
    const reactivateMutation = useReactivateSite();

    const sites = data?.data || [];
    const totalPages = data?.pagination?.totalPages || 1;
    const total = data?.pagination?.total || 0;

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, statusFilter]);

    const handleConfirmAction = async () => {
        if (!confirmAction) {
            return;
        }
        if (confirmAction.action === 'suspend') {
            await suspendMutation.mutateAsync(confirmAction.siteId);
        } else {
            await reactivateMutation.mutateAsync(confirmAction.siteId);
        }
        setConfirmAction(null);
    };

    const isActionLoading = suspendMutation.isLoading || reactivateMutation.isLoading;

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="studio-content-page studio-admin-page studio-admin-directory-page">
            <Dialog open={!!selectedSite} onOpenChange={(open) => {
                if (!open) {
                    setSelectedSite(null);
                }
            }}>
                <DialogContent className="studio-active studio-create-dialog studio-overrides-dialog" style={{maxHeight: '90vh', overflowY: 'auto'}}>
                    <DialogClose aria-label="Close publication details" className="studio-create-dialog-close">×</DialogClose>
                    <DialogHeader><DialogTitle>Publication details</DialogTitle><DialogDescription>Manage this publication’s plan, features, and limits.</DialogDescription></DialogHeader>
                    {selectedSite && <OverridesPanel key={selectedSite.id} scope="sites" subjectId={selectedSite.id} />}
                </DialogContent>
            </Dialog>
            <header className="studio-page-heading studio-admin-page-heading">
                <div>
                    <p className="studio-eyebrow">Publication registry</p>
                    <h1>All publications</h1>
                    <p className="studio-admin-intro">Monitor ownership, lifecycle status, and platform access for every publication.</p>
                </div>
                {!isLoading && <span className="studio-admin-heading-count"><LucideIcon.Newspaper size={16} /><strong>{formatNumber(total)}</strong>{total === 1 ? 'publication' : 'publications'}</span>}
            </header>

            <div className="studio-admin-toolbar">
                <label className="studio-admin-search">
                    <LucideIcon.Search size={16} />
                    <input aria-label="Search publications"
                        className="studio-field-control"
                        placeholder={isCompact ? 'Search…' : 'Search by name, slug, or owner…'}
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                </label>
                <div className="studio-admin-toolbar-actions">
                    {(searchInput || statusFilter) && (
                        <button className="studio-control studio-admin-clear-filter"
                            type="button"
                            onClick={() => {
                                setSearchInput('');
                                setStatusFilter('');
                            }}
                        >
                            Clear
                        </button>
                    )}

                    <Select
                        ariaLabel="Filter publications by status"
                        options={STATUS_OPTIONS}
                        placeholder="All statuses"
                        value={statusFilter}
                        onChange={setStatusFilter}
                    />
                </div>
            </div>

            {isError && (
                <div className="studio-admin-error" role="alert">
                    {queryError instanceof Error ? queryError.message : 'Failed to load publications'}
                </div>
            )}

            <div className="studio-panel studio-admin-data-panel"
                style={{background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, overflow: 'hidden', width: '100%'}}
            >
                {/* Table header - hide on mobile */}
                {!isCompact && (
                    <div className="studio-admin-data-header studio-admin-sites-header"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1fr 1fr',
                            gap: 12,
                            padding: '12px 24px',
                            borderBottom: '1px solid var(--tt-card-border)',
                            background: 'var(--tt-table-header-bg)'
                        }}
                    >
                        {['Name', 'Slug', 'Owner', 'Status', 'Created', 'Actions'].map((header, i) => (
                            <span
                                key={header}
                                style={{
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    color: 'var(--tt-text-secondary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.4px',
                                    textAlign: i === 5 ? 'right' : 'left'
                                }}
                            >
                                {header}
                            </span>
                        ))}
                    </div>
                )}

                {isLoading ? (
                    <div style={{padding: isCompact ? '32px 16px' : '48px 24px', textAlign: 'center'}}>
                        <p style={{fontSize: '1.4rem', color: 'var(--tt-text-secondary)'}}>Loading publications...</p>
                    </div>
                ) : sites.length === 0 ? (
                    <div style={{padding: isCompact ? '32px 16px' : '48px 24px', textAlign: 'center'}}>
                        <p style={{fontSize: '1.4rem', color: 'var(--tt-text-secondary)'}}>
                            {debouncedSearch || statusFilter ? 'No publications match your filters' : 'No publications found'}
                        </p>
                    </div>
                ) : isCompact ? (
                    /* Mobile card layout */
                    sites.map((site: AdminSite) => (
                        <div key={site.id}
                            className="studio-admin-data-row is-mobile"
                            style={{
                                padding: '14px 16px',
                                borderBottom: '1px solid var(--tt-divider)'
                            }}
                        >
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6}}>
                                <div style={{flex: 1, minWidth: 0}}>
                                    <div style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)'}}>
                                        <button className="studio-control" type="button" onClick={() => setSelectedSite(site)}>{site.name}</button>
                                    </div>
                                    <div style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)', marginTop: 2}}>
                                        {site.slug}.tribetale.com
                                    </div>
                                </div>
                                <SiteStatusBadge status={site.status} />
                            </div>
                            <div style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)', marginBottom: 8}}>
                                Owner: {site.owner_name || site.owner_email}
                            </div>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <div style={{fontSize: '1.1rem', color: 'var(--tt-text-tertiary)'}}>
                                    {formatDate(site.created_at)}
                                </div>
                                {site.status === 'active' && (
                                    <button className="studio-control" style={{height: 28, padding: '0 10px', borderRadius: 8, background: 'none', border: '1px solid rgba(245, 11, 35, 0.3)', color: '#F50B23', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'}}
                                        type="button"
                                        onClick={() => setConfirmAction({
                                            siteId: site.id,
                                            siteName: site.name,
                                            action: 'suspend'
                                        })
                                        }
                                    >
                                        Suspend
                                    </button>
                                )}
                                {site.status === 'suspended' && (
                                    <button className="studio-control" style={{height: 28, padding: '0 10px', borderRadius: 8, background: 'none', border: '1px solid var(--studio-soft)', color: 'var(--tt-text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'}}
                                        type="button"
                                        onClick={() => setConfirmAction({
                                            siteId: site.id,
                                            siteName: site.name,
                                            action: 'reactivate'
                                        })
                                        }
                                    >
                                        Reactivate
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    /* Desktop table layout */
                    sites.map((site: AdminSite) => (
                        <div key={site.id}
                            className="studio-admin-data-row studio-admin-site-row"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1fr 1fr',
                                gap: 12,
                                padding: '16px 24px',
                                alignItems: 'center',
                                borderBottom: '1px solid var(--tt-divider)',
                                transition: 'background 0.1s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--tt-table-header-bg)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <div style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)'}}>
                                <button className="studio-control" type="button" onClick={() => setSelectedSite(site)}>{site.name}</button>
                            </div>

                            <div style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)'}}>
                                {site.slug}
                            </div>

                            <div>
                                <div style={{fontSize: '1.3rem', fontWeight: 500, color: 'var(--tt-text-primary)'}}>
                                    {site.owner_name || 'Unknown'}
                                </div>
                                <div style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)', marginTop: 1}}>
                                    {site.owner_email}
                                </div>
                            </div>

                            <div>
                                <SiteStatusBadge status={site.status} />
                            </div>

                            <div style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)'}}>
                                {formatDate(site.created_at)}
                            </div>

                            <div style={{textAlign: 'right'}}>
                                {site.status === 'active' && (
                                    <button className="studio-control" style={{height: 30, padding: '0 12px', borderRadius: 8, background: 'none', border: '1px solid rgba(245, 11, 35, 0.3)', color: '#F50B23', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'}}
                                        type="button"
                                        onClick={() => setConfirmAction({
                                            siteId: site.id,
                                            siteName: site.name,
                                            action: 'suspend'
                                        })
                                        }
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(245, 11, 35, 0.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'none';
                                        }}
                                    >
                                        Suspend
                                    </button>
                                )}
                                {site.status === 'suspended' && (
                                    <button className="studio-control" style={{height: 30, padding: '0 12px', borderRadius: 8, background: 'none', border: '1px solid var(--studio-soft)', color: 'var(--tt-text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'}}
                                        type="button"
                                        onClick={() => setConfirmAction({
                                            siteId: site.id,
                                            siteName: site.name,
                                            action: 'reactivate'
                                        })
                                        }
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'var(--studio-soft)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'none';
                                        }}
                                    >
                                        Reactivate
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {totalPages > 1 && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: isCompact ? 12 : 16,
                        marginTop: isCompact ? 16 : 24,
                        flexWrap: 'wrap'
                    }}
                >
                    <button className="studio-control" disabled={page === 1}
                        style={{height: 34, padding: '0 14px', borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: page === 1 ? 'var(--tt-btn-disabled-text)' : 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: page === 1 ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        {isCompact ? '←' : 'Previous'}
                    </button>
                    <span style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)'}}>
                        {formatNumber(page)} / {formatNumber(totalPages)}
                    </span>
                    <button className="studio-control" disabled={page === totalPages}
                        style={{height: 34, padding: '0 14px', borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: page === totalPages ? 'var(--tt-btn-disabled-text)' : 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: page === totalPages ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                        {isCompact ? '→' : 'Next'}
                    </button>
                </div>
            )}

            {confirmAction && (
                <ConfirmDialog
                    confirmColor={confirmAction.action === 'suspend' ? '#F50B23' : 'var(--tt-text-secondary)'}
                    confirmLabel={confirmAction.action === 'suspend' ? 'Suspend' : 'Reactivate'}
                    isLoading={isActionLoading}
                    message={
                        confirmAction.action === 'suspend'
                            ? `Are you sure you want to suspend "${confirmAction.siteName}"? The publication will become inaccessible to its owner and visitors.`
                            : `Are you sure you want to reactivate "${confirmAction.siteName}"? The publication will become accessible again.`
                    }
                    title={confirmAction.action === 'suspend' ? 'Suspend publication' : 'Reactivate publication'}
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={handleConfirmAction}
                />
            )}
        </div>
    );
}
